import { useState, useContext, useEffect, useRef } from "react";
import {
  Container,
  Typography,
  Box,
  TextField,
  Button,
  Paper,
  Alert,
  Snackbar,
  Divider,
  IconButton,
  Tooltip,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import Parser from "html-react-parser";
import RichText from "../../common/RichTextControl";
import { UserPage } from "../../models/UserPage";
import { PageUpdate } from "../../models/PageUpdate";
import { getBackend } from "../../service/baseService";
import {
  updatePage,
  uploadSplashImage,
  deleteSplashImage,
  getSplashImageSrc,
  SPLASH_IMAGE_MAX_BYTES,
  SPLASH_IMAGE_TYPES,
  uploadVideo,
  deleteVideo,
  getVideoSrc,
  VIDEO_MAX_BYTES,
  VIDEO_TYPES,
  getPageUpdates,
  createPageUpdate,
  deletePageUpdate,
  uploadUpdateImage,
  getUpdateImageSrc,
  UPDATE_IMAGE_MAX_BYTES,
  UPDATE_IMAGE_TYPES,
} from "../../service/UserPageService";
import { useNavigate, useLocation } from "react-router-dom";
import { UserContextType, useAuth } from "../../auth";
import AiRewriteButton from "../../common/AiRewriteButton";
import { useProofreadCheck } from "../../common/useProofreadCheck";

const Emitter = require("tiny-emitter/instance");

const CreateUserPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { getToken, IsAdmin } = useContext<UserContextType>(useAuth);

  const [userPage, setUserPage] = useState<UserPage>({
    title: "",
    description: "",
    shortName: "",
  } as UserPage);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isEditMode, setIsEditMode] = useState(false);

  // Splash graphic: a newly chosen file (uploaded after the page is saved), or a
  // request to remove the page's existing one.
  const [splashFile, setSplashFile] = useState<File | null>(null);
  const [splashPreview, setSplashPreview] = useState<string | null>(null);
  const [removeSplash, setRemoveSplash] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Video: same pattern as the splash graphic -- a newly chosen file (uploaded
  // after the page is saved), or a request to remove the page's existing one.
  const [videoFile, setVideoFile] = useState<File | null>(null);
  const [videoPreview, setVideoPreview] = useState<string | null>(null);
  const [removeVideo, setRemoveVideo] = useState(false);
  const videoInputRef = useRef<HTMLInputElement>(null);

  const [updates, setUpdates] = useState<PageUpdate[]>([]);
  const [newUpdateTitle, setNewUpdateTitle] = useState("");
  const [newUpdateBody, setNewUpdateBody] = useState("");
  const [addingUpdate, setAddingUpdate] = useState(false);

  const pageProofread = useProofreadCheck("page", "page");
  const updateProofread = useProofreadCheck("page-update", "update");

  // Pictures for the update being composed; uploaded once the update is created.
  const [newUpdateImages, setNewUpdateImages] = useState<{ file: File; preview: string }[]>([]);
  const updateImageInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (location.state && location.state.page) {
      setUserPage(location.state.page);
      setIsEditMode(true);
    }
  }, [location]);

  useEffect(() => {
    return () => {
      if (splashPreview) URL.revokeObjectURL(splashPreview);
    };
  }, [splashPreview]);

  useEffect(() => {
    return () => {
      if (videoPreview) URL.revokeObjectURL(videoPreview);
    };
  }, [videoPreview]);

  const existingSplashSrc = removeSplash ? undefined : getSplashImageSrc(userPage.splashImageUrl);
  const displayedSplash = splashPreview || existingSplashSrc;

  const existingVideoSrc = removeVideo ? undefined : getVideoSrc(userPage.videoUrl);
  const displayedVideo = videoPreview || existingVideoSrc;

  const onSplashFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;

    if (!SPLASH_IMAGE_TYPES.includes(file.type)) {
      setError("Splash graphic must be a JPEG, PNG, GIF or WebP image.");
      return;
    }
    if (file.size > SPLASH_IMAGE_MAX_BYTES) {
      setError("Splash graphic must be 10 MB or smaller.");
      return;
    }

    setError(null);
    setSplashFile(file);
    setSplashPreview(URL.createObjectURL(file));
    setRemoveSplash(false);
  };

  const onRemoveSplash = () => {
    setSplashFile(null);
    setSplashPreview(null);
    setRemoveSplash(!!userPage.splashImageUrl);
  };

  const onVideoFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;

    if (!VIDEO_TYPES.includes(file.type)) {
      setError("Video must be an MP4, MOV or WebM file.");
      return;
    }
    if (file.size > VIDEO_MAX_BYTES) {
      setError(`Video must be ${VIDEO_MAX_BYTES / (1024 * 1024)} MB or smaller.`);
      return;
    }

    setError(null);
    setVideoFile(file);
    setVideoPreview(URL.createObjectURL(file));
    setRemoveVideo(false);
  };

  const onRemoveVideo = () => {
    setVideoFile(null);
    setVideoPreview(null);
    setRemoveVideo(!!userPage.videoUrl);
  };

  const onUpdateImagesChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files ?? []);
    event.target.value = "";
    if (files.length === 0) return;

    if (files.some((file) => !UPDATE_IMAGE_TYPES.includes(file.type))) {
      Emitter.emit("notify", "Pictures must be JPEG, PNG, GIF or WebP images.", "error");
      return;
    }
    if (files.some((file) => file.size > UPDATE_IMAGE_MAX_BYTES)) {
      Emitter.emit("notify", `Each picture must be ${UPDATE_IMAGE_MAX_BYTES / (1024 * 1024)} MB or smaller.`, "error");
      return;
    }

    setNewUpdateImages((prev) => [
      ...prev,
      ...files.map((file) => ({ file, preview: URL.createObjectURL(file) })),
    ]);
  };

  const onRemoveUpdateImage = (index: number) => {
    setNewUpdateImages((prev) => {
      URL.revokeObjectURL(prev[index].preview);
      return prev.filter((_, i) => i !== index);
    });
  };

  const fetchUpdates = async (pageId: string) => {
    try {
      const data = await getPageUpdates(pageId);
      setUpdates(data);
    } catch (err) {
      console.error("Failed to load page updates", err);
    }
  };

  useEffect(() => {
    if (isEditMode && userPage.id) {
      fetchUpdates(userPage.id);
    }
  }, [isEditMode, userPage.id]);

  const onAddUpdate = async (event: React.FormEvent) => {
    event.preventDefault();

    if (!newUpdateTitle || !newUpdateBody) {
      Emitter.emit("notify", "Please fill in both a title and content for the update.", "error");
      return;
    }

    if (!getToken || !userPage.id) {
      return;
    }

    await updateProofread.checkThenSave(newUpdateBody, saveUpdate);
  };

  const saveUpdate = async (body: string) => {
    if (!getToken || !userPage.id) {
      return;
    }

    setNewUpdateBody(body);
    setAddingUpdate(true);
    try {
      const created: PageUpdate = await createPageUpdate(
        { pageId: userPage.id, title: newUpdateTitle, body },
        getToken()
      );

      let failedImages = 0;
      for (const image of newUpdateImages) {
        try {
          await uploadUpdateImage(created.id, image.file, getToken());
        } catch (err) {
          console.error("Failed to upload update picture", err);
          failedImages++;
        }
      }

      if (failedImages > 0) {
        Emitter.emit(
          "notify",
          `Update added, but ${failedImages} of ${newUpdateImages.length} pictures could not be uploaded.`,
          "warning"
        );
      } else {
        Emitter.emit("notify", "Update added.", "success");
      }

      newUpdateImages.forEach((image) => URL.revokeObjectURL(image.preview));
      setNewUpdateImages([]);
      setNewUpdateTitle("");
      setNewUpdateBody("");
      fetchUpdates(userPage.id);
    } catch (err) {
      console.error("Failed to add update", err);
      Emitter.emit("notify", "Failed to add update.", "error");
    } finally {
      setAddingUpdate(false);
    }
  };

  const onDeleteUpdate = async (id: string) => {
    if (!getToken || !userPage.id) {
      return;
    }

    if (!window.confirm("Delete this update? This cannot be undone.")) {
      return;
    }

    try {
      await deletePageUpdate(id, getToken());
      Emitter.emit("notify", "Update deleted.", "success");
      fetchUpdates(userPage.id);
    } catch (err) {
      console.error("Failed to delete update", err);
      Emitter.emit("notify", "Failed to delete update.", "error");
    }
  };

  const onBodyChange = (text: string) => {
    setUserPage((prev) => ({ ...prev, description: text }));
  };

  const onTitleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setUserPage((prev) => ({ ...prev, title: event.target.value }));
  };

  const onSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setError(null);

    if (!userPage.title || !userPage.description || !userPage.shortName) {
      setError("Please fill in all fields (Title, Short Name, Content).");
      return;
    }

    if (!getToken) {
      setError("Not authenticated.");
      return;
    }

    await pageProofread.checkThenSave(userPage.description, savePage);
  };

  const savePage = async (description: string) => {
    if (!getToken) {
      return;
    }

    const pageToSave = { ...userPage, description };
    setUserPage(pageToSave);
    setLoading(true);

    try {
      const config = {
        headers: {
          Authorization: `Bearer ${getToken()}`,
          "Content-Type": "application/json",
        },
      };

      let pageId = userPage.id;
      if (isEditMode) {
        await updatePage(pageToSave, getToken());
      } else {
        const response = await getBackend().post("/api/pages", pageToSave, config);
        pageId = response.data.id;
        // From here on the page exists, so a retry after a failed image upload must update, not re-create it.
        setUserPage((prev) => ({ ...prev, id: pageId }));
        setIsEditMode(true);
      }

      try {
        if (splashFile) {
          const saved = await uploadSplashImage(pageId, splashFile, getToken());
          setUserPage((prev) => ({ ...prev, splashImageUrl: saved.splashImageUrl }));
          setSplashFile(null);
          setSplashPreview(null);
        } else if (removeSplash) {
          await deleteSplashImage(pageId, getToken());
          setUserPage((prev) => ({ ...prev, splashImageUrl: undefined }));
          setRemoveSplash(false);
        }
      } catch (err: any) {
        console.error(err);
        setError("The page was saved, but the splash graphic could not be updated. Please try again.");
        return;
      }

      try {
        if (videoFile) {
          const saved = await uploadVideo(pageId, videoFile, getToken());
          setUserPage((prev) => ({ ...prev, videoUrl: saved.videoUrl }));
          setVideoFile(null);
          setVideoPreview(null);
        } else if (removeVideo) {
          await deleteVideo(pageId, getToken());
          setUserPage((prev) => ({ ...prev, videoUrl: undefined }));
          setRemoveVideo(false);
        }
      } catch (err: any) {
        console.error(err);
        setError("The page was saved, but the video could not be updated. Please try again.");
        return;
      }

      Emitter.emit("notify", `Page ${isEditMode ? "updated" : "created"} successfully!`, "success");
      navigate("/ManageUserPage");
    } catch (err: any) {
      console.error(err);
      setError(`Failed to ${isEditMode ? 'update' : 'create'} page. Please try again.`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
      <Paper elevation={3} sx={{ p: 4, borderRadius: 2 }}>
        <Typography variant="h4" component="h1" gutterBottom align="center" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
          {isEditMode ? "Edit Page" : "Create New Page"}
        </Typography>
        <Typography variant="body1" color="textSecondary" align="center" sx={{ mb: 4 }}>
          {isEditMode ? "Update the details of your custom page." : "Create a custom page that will appear in the navigation bar."}
        </Typography>

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        <Box component="form" onSubmit={onSubmit}>
          <Typography variant="h6" gutterBottom>
            Page Title
          </Typography>
          <TextField
            fullWidth
            margin="normal"
            required
            label="Enter page title (e.g. 'Youth Ministry Fall Retreat')"
            variant="outlined"
            value={userPage.title}
            onChange={onTitleChange}
            sx={{ mb: 3 }}
          />

          <Typography variant="h6" gutterBottom>
            Short Name (Toolbar Name)
          </Typography>
          <TextField
            fullWidth
            margin="normal"
            required
            label="Enter short name for navbar and URL (e.g. 'YouthRetreat')"
            variant="outlined"
            value={userPage.shortName || ""}
            onChange={(e) => setUserPage(prev => ({ ...prev, shortName: e.target.value }))}
            helperText="This will be used in the URL and the navigation bar. Must be unique."
            sx={{ mb: 3 }}
          />

          <Typography variant="h6" gutterBottom>
            Expiration Date (Optional)
          </Typography>
          <TextField
            fullWidth
            margin="normal"
            type="date"
            InputLabelProps={{ shrink: true }}
            label="Date when this page should stop showing in the navbar"
            variant="outlined"
            value={userPage.expirationDate ? new Date(userPage.expirationDate).toISOString().split('T')[0] : ""}
            onChange={(e) => setUserPage(prev => ({ ...prev, expirationDate: e.target.value ? new Date(e.target.value) : undefined }))}
            sx={{ mb: 3 }}
          />

          <Typography variant="h6" gutterBottom>
            Splash Graphic (Optional)
          </Typography>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
            Shown as the banner behind the page title. Wide images (e.g. 1920×600) work best. JPEG, PNG, GIF or WebP, up to 10 MB.
          </Typography>
          {displayedSplash && (
            <Box
              component="img"
              src={displayedSplash}
              alt="Splash graphic preview"
              sx={{ width: "100%", maxHeight: 240, objectFit: "cover", borderRadius: 1, mb: 1, boxShadow: 1 }}
            />
          )}
          <Box sx={{ display: "flex", gap: 2, mb: 3 }}>
            <input
              ref={fileInputRef}
              type="file"
              accept={SPLASH_IMAGE_TYPES.join(",")}
              hidden
              onChange={onSplashFileChange}
            />
            <Button variant="outlined" onClick={() => fileInputRef.current?.click()} disabled={loading}>
              {displayedSplash ? "Replace Image" : "Upload Image"}
            </Button>
            {displayedSplash && (
              <Button variant="outlined" color="error" onClick={onRemoveSplash} disabled={loading}>
                Remove Image
              </Button>
            )}
          </Box>

          <Typography variant="h6" gutterBottom>
            Video (Optional)
          </Typography>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
            Shown as its own card below the page's updates. MP4, MOV or WebM, up to 200 MB.
          </Typography>
          {displayedVideo && (
            <Box
              component="video"
              src={displayedVideo}
              controls
              sx={{ width: "100%", maxHeight: 320, borderRadius: 1, mb: 1, boxShadow: 1, backgroundColor: "black" }}
            />
          )}
          <Box sx={{ display: "flex", gap: 2, mb: 3 }}>
            <input
              ref={videoInputRef}
              type="file"
              accept={VIDEO_TYPES.join(",")}
              hidden
              onChange={onVideoFileChange}
            />
            <Button variant="outlined" onClick={() => videoInputRef.current?.click()} disabled={loading}>
              {displayedVideo ? "Replace Video" : "Upload Video"}
            </Button>
            {displayedVideo && (
              <Button variant="outlined" color="error" onClick={onRemoveVideo} disabled={loading}>
                Remove Video
              </Button>
            )}
          </Box>

          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
            <Typography variant="h6">
              Page Content
            </Typography>
            <AiRewriteButton
              content={userPage.description}
              contentType="page"
              isHtml
              disabled={loading}
              onAccept={onBodyChange}
            />
          </Box>
          <Box sx={{ border: "1px solid #ccc", borderRadius: 1, minHeight: "200px", mb: 3 }}>
            <RichText onChange={onBodyChange} text={userPage.description} />
          </Box>

          <Button
            type="submit"
            fullWidth
            variant="contained"
            size="large"
            disabled={loading || pageProofread.isChecking}
            sx={{ py: 1.5, fontWeight: 'bold' }}
          >
            {pageProofread.isChecking
              ? "Checking spelling..."
              : loading ? (isEditMode ? "Updating..." : "Creating...") : (isEditMode ? "Update Page" : "Create Page")}
          </Button>
          <Button
            fullWidth
            variant="outlined"
            sx={{ mt: 2 }}
            onClick={() => navigate("/ManageUserPage")}
          >
            Cancel
          </Button>
        </Box>
      </Paper>

      {isEditMode && userPage.id && (
        <Paper elevation={3} sx={{ p: 4, borderRadius: 2, mt: 4 }}>
          <Typography variant="h5" component="h2" gutterBottom sx={{ fontWeight: "bold", color: "primary.main" }}>
            Updates
          </Typography>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
            Updates appear underneath the main content on the page, newest first, without changing the page above.
          </Typography>

          <Box component="form" onSubmit={onAddUpdate} sx={{ mb: 4 }}>
            <TextField
              fullWidth
              margin="normal"
              label="Update title"
              variant="outlined"
              value={newUpdateTitle}
              onChange={(e) => setNewUpdateTitle(e.target.value)}
              sx={{ mb: 2 }}
            />
            <Box sx={{ border: "1px solid #ccc", borderRadius: 1, minHeight: "120px", mb: 2 }}>
              <RichText onChange={setNewUpdateBody} text={newUpdateBody} />
            </Box>
            <Box sx={{ mb: 2 }}>
              <AiRewriteButton
                content={newUpdateBody}
                contentType="page-update"
                isHtml
                disabled={addingUpdate}
                onAccept={setNewUpdateBody}
              />
            </Box>
            <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
              Pictures (optional) are shown below the update. JPEG, PNG, GIF or WebP, up to 10 MB each.
            </Typography>
            {newUpdateImages.length > 0 && (
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 1 }}>
                {newUpdateImages.map((image, index) => (
                  <Box key={image.preview} sx={{ position: "relative" }}>
                    <Box
                      component="img"
                      src={image.preview}
                      alt={image.file.name}
                      sx={{ width: 120, height: 90, objectFit: "cover", borderRadius: 1, boxShadow: 1, display: "block" }}
                    />
                    <Tooltip title="Remove Picture">
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => onRemoveUpdateImage(index)}
                        disabled={addingUpdate}
                        sx={{ position: "absolute", top: 2, right: 2, bgcolor: "rgba(255,255,255,0.85)", "&:hover": { bgcolor: "#fff" } }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                ))}
              </Box>
            )}
            <input
              ref={updateImageInputRef}
              type="file"
              accept={UPDATE_IMAGE_TYPES.join(",")}
              multiple
              hidden
              onChange={onUpdateImagesChange}
            />
            <Box sx={{ mb: 2 }}>
              <Button variant="outlined" onClick={() => updateImageInputRef.current?.click()} disabled={addingUpdate}>
                Add Pictures
              </Button>
            </Box>
            <Button type="submit" variant="contained" disabled={addingUpdate || updateProofread.isChecking}>
              {updateProofread.isChecking ? "Checking spelling..." : addingUpdate ? "Adding..." : "Add Update"}
            </Button>
          </Box>

          <Divider sx={{ mb: 3 }} />

          {updates.length === 0 ? (
            <Typography color="textSecondary">No updates yet.</Typography>
          ) : (
            updates.map((update) => (
              <Box key={update.id} sx={{ mb: 3, pb: 2, borderBottom: "1px solid #eee" }}>
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                  <Box>
                    <Typography variant="h6">{update.title}</Typography>
                    <Typography variant="caption" color="textSecondary">
                      {update.createdAt ? new Date(update.createdAt).toLocaleString() : ""}
                      {update.createdBy ? ` · ${update.createdBy}` : ""}
                    </Typography>
                  </Box>
                  {IsAdmin && IsAdmin() && (
                    <Tooltip title="Delete Update">
                      <IconButton color="error" onClick={() => onDeleteUpdate(update.id)}>
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                  )}
                </Box>
                <Box className="rich-text-content" sx={{ mt: 1, color: "text.secondary" }}>
                  {Parser(update.body)}
                </Box>
                {update.images && update.images.length > 0 && (
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mt: 1 }}>
                    {update.images.map((image) => (
                      <Box
                        key={image.id}
                        component="img"
                        src={getUpdateImageSrc(image.url)}
                        alt=""
                        sx={{ width: 120, height: 90, objectFit: "cover", borderRadius: 1, boxShadow: 1 }}
                      />
                    ))}
                  </Box>
                )}
              </Box>
            ))
          )}
        </Paper>
      )}
      {pageProofread.proofreadDialog}
      {updateProofread.proofreadDialog}
    </Container>
  );
};

export default CreateUserPage;
